import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKelimeTamamlamaOyunu } from './hooks/useKelimeTamamlamaOyunu';
import Header from './components/Header';
import Controls from './components/Controls';
import GameDisplay from './components/GameDisplay';
import Message from './components/Message';
import Description from './components/Description';
import GameOverModal from './components/GameOverModal';
import './styles/KelimeTamamlamaOyunu.css';
import axios from 'axios';
import axiosInstance from '../../../utils/axiosInstance';
import { API_URLS } from '../../../config/config';

const KelimeTamamlamaOyunu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stage, exercise } = location.state || {};

  const token = localStorage.getItem('userToken');

  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [showInstructions, setShowInstructions] = useState(true);
  const [showGameOverModal, setShowGameOverModal] = useState(false);

  // Aşamaya göre zorluk ayarlama
  const stageNumber = stage?.number || null;
  let initialDifficulty = 'easy';
  let difficultyLocked = false;

  if (stageNumber) {
    difficultyLocked = true;
    if (stageNumber >= 1 && stageNumber <= 7) {
      initialDifficulty = 'easy';
    } else if (stageNumber >= 8 && stageNumber <= 14) {
      initialDifficulty = 'medium';
    } else {
      initialDifficulty = 'hard';
    }
  }

  const {
    difficulty,
    setDifficulty,
    paragraphsList,
    selectedParagraphIndex,
    setSelectedParagraphIndex,
    paragraph,
    options,
    userSelections,
    isPlaying,
    score,
    message,
    timeRemaining,
    handleWordSelect,
    handleBlankClick,
    handleDifficultyChange: originalHandleDifficultyChange,
    handleParagraphChange,
    getParagraphWithBlanks,
    handlePlayPause,
    handleReset,
  } = useKelimeTamamlamaOyunu(userData, initialDifficulty);

  // Kullanıcı verilerini çekme (Önce öğrenci, yoksa öğretmen)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const studentResponse = await axiosInstance.get(`${API_URLS.STUDENT_URL}/me`);
        if (
          studentResponse.status === 200 &&
          studentResponse.data &&
          studentResponse.data.status === 'active'
        ) {
          const studentData = studentResponse.data;
          setUserData(studentData);
          setUserRole('student');
          return;
        } else {
          setUserData(null);
          setUserRole(null);
        }
      } catch (error) {
        console.error('Öğrenci verisi alınırken hata oluştu:', error);
      }

      // Öğrenci yoksa veya aktif değilse öğretmen verisini kontrol et
      try {
        const teacherResponse = await fetch(`${API_URLS.TEACHER_URL}/me`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (teacherResponse.ok) {
          const teacherData = await teacherResponse.json();
          setUserData(teacherData);
          setUserRole('teacher');
          return;
        }
      } catch (error) {
        console.error('Öğretmen verisi alınırken hata oluştu:', error);
      }

      setUserData(null);
      setUserRole(null);
    };

    fetchUserData();
  }, [token]);

  const startGame = () => {
    setShowInstructions(false);
    handleReset(); // Oyunu sıfırla
  };

  const updateProgress = async () => {
    if (!userData || !userRole) return;
    try {
      const payload = {
        stageNumber: stage?.number || 1,
        gameName: exercise?.gameName || 'Kelime Tamamlama',
        score: score,
        time: (userData && userData.status === 'inactive' ? 15 : 60) - timeRemaining,
        completed: true,
      };

      if (userRole === 'student') {
        // Öğrenci: cookie üzerinden token otomatik, axiosInstance kullan
        const apiUrl = `${API_URLS.STUDENT_URL}akillikurs/progress/update-game`;
        await axiosInstance.post(apiUrl, payload);
      } else {
        // Öğretmen: localStorage token, Authorization header ile axios kullan
        const apiUrl = `${API_URLS.TEACHER_URL}akillikurs/progress/update-game`;
        const teacherToken = localStorage.getItem('userToken');
        await axios.post(apiUrl, payload, {
          headers: {
            Authorization: `Bearer ${teacherToken}`,
            'Content-Type': 'application/json',
          },
        });
      }

      console.log('İlerleme başarıyla güncellendi.');
    } catch (error) {
      console.error('İlerleme güncellenirken hata oluştu:', error);
    }
  };

  const onGameOver = () => {
    setShowGameOverModal(true);
    updateProgress();
  };

  const handleRestart = () => {
    setShowGameOverModal(false);
    handleReset();
  };

  // Süre dolduğunda veya tüm kelimeler tamamlandığında oyunu bitir
  useEffect(() => {
    if (
      (!isPlaying && timeRemaining === 0) ||
      message === 'Tebrikler! Tüm kelimeleri doldurdunuz.'
    ) {
      onGameOver();
    }
  }, [isPlaying, timeRemaining, message]);

  const handleReturnToPreviousStage = () => {
    if (stage) {
      navigate(
        userRole === 'student' ? '/ogrenci-dashboard' : '/ogretmen-dashboard',
        {
          state: {
            selectedMenu: 'ExerciseList',
            selectedStage: stage,
          },
        }
      );
    } else {
      alert('Aşama bilgisi bulunmamaktadır.');
    }
  };

  const handleClose = () => {
    setShowGameOverModal(false);
  };

  // Zorluk değiştirirken aşama varsa izin verme
  const handleDifficultyChange = (e) => {
    if (difficultyLocked) return;
    originalHandleDifficultyChange(e);
  };

  return (
    <div className="kayu-kelime-tamamlama-container">
      {showInstructions ? (
        <div className="kayu-intro-container">
          <div className="kayu-card">
            <div className="kayu-card-body">
              <Header handleReturnToPreviousStage={handleReturnToPreviousStage} />
              <h1 className="kayu-title">Kelime Tamamlama Çalışması</h1>
              {stage ? (
                <p className="kayu-description">
                  Bu çalışmada aşamanıza göre zorluk seviyesi otomatik belirlenecek. Cümlelerdeki boşluklara doğru kelimeleri yerleştirin.
                </p>
              ) : (
                <Description />
              )}
              <button className="kayu-btn" onClick={startGame}>
                BAŞLAT
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Header handleReturnToPreviousStage={handleReturnToPreviousStage} />
          <div className="kayu-card">
            <div className="kayu-card-body">
              <Controls
                isPlaying={isPlaying}
                handlePlayPause={handlePlayPause}
                handleReset={handleReset}
                timeRemaining={timeRemaining}
                difficulty={difficulty}
                handleDifficultyChange={handleDifficultyChange}
                selectedParagraphIndex={selectedParagraphIndex}
                handleParagraphChange={(e) => setSelectedParagraphIndex(Number(e.target.value))}
                paragraphsList={paragraphsList}
                difficultyLocked={difficultyLocked}
              />
              <GameDisplay
                paragraph={paragraph}
                getParagraphWithBlanks={getParagraphWithBlanks}
                options={options}
                selectedWord={userSelections.selectedWord}
                userSelections={userSelections}
                handleWordSelect={handleWordSelect}
                handleBlankClick={handleBlankClick}
              />
              <Message message={message} timeRemaining={timeRemaining} />
            </div>
          </div>
        </>
      )}

      {showGameOverModal && (
        <GameOverModal
          score={score}
          onRestart={handleRestart}
          onClose={handleClose}
          userData={userData}
        />
      )}
    </div>
  );
};

export default KelimeTamamlamaOyunu;
